import { useCallback, useEffect, useRef } from "react";
import * as retailRecordingService from "../services/retailRecordingService";
import {
  clearSession as clearChunkStorageSession,
  getPendingChunks,
  markChunksUploaded,
  saveChunk,
  saveSession as saveChunkStorageSession,
} from "../utils/recordingChunkStorage";
import type { ChunkRecord } from "../utils/recordingChunkStorage";
import {
  clearSession as clearLocalSession,
  saveSession as saveLocalSession,
  updateNextChunkIndex,
} from "../utils/recordingSessionStorage";
import { isFatalUploadError } from "../utils/recordingErrors";
import { reportRecordingError } from "../utils/reportRecordingError";
import { getLocalDateString } from "../helpers/getLocalDateString";
import { BATCH_UPLOAD_MAX_BYTES, BULK_UPLOAD_INTERVAL_MS } from "../constants";

const MAX_UPLOAD_RETRIES = 5;
const INITIAL_RETRY_DELAY_MS = 1000;

// Splits pending chunks into batches whose summed decoded byte size stays under
// the batch upload cap. Chunks are assumed to arrive sorted by chunkIndex; a
// single oversized chunk (should never happen for a 15s slice) forms its own
// batch rather than being dropped.
const groupChunksIntoBatches = (
  chunks: ChunkRecord[],
  maxBatchBytes: number
): ChunkRecord[][] => {
  const batches: ChunkRecord[][] = [];
  let currentBatch: ChunkRecord[] = [];
  let currentBytes = 0;

  chunks.forEach((chunk) => {
    const chunkBytes = chunk.blob.size;
    if (currentBatch.length > 0 && currentBytes + chunkBytes > maxBatchBytes) {
      batches.push(currentBatch);
      currentBatch = [];
      currentBytes = 0;
    }
    currentBatch.push(chunk);
    currentBytes += chunkBytes;
  });

  if (currentBatch.length > 0) {
    batches.push(currentBatch);
  }

  return batches;
};

const sleep = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

type UseChunkedAudioUploadOptions = {
  contactId: string;
  getHybridToken: () => string;
  onUploadFatalError?: (error: unknown) => void;
};

const useChunkedAudioUpload = ({
  contactId,
  getHybridToken,
  onUploadFatalError,
}: UseChunkedAudioUploadOptions) => {
  const recordingIdRef = useRef<string | null>(null);
  const mimeTypeRef = useRef<string | null>(null);
  const isUploadingRef = useRef(false);
  const uploadHaltedRef = useRef(false);
  const nextChunkIndexRef = useRef(0);
  const flushTimerRef = useRef<number | null>(null);
  // Tracks whether we've already logged the current transient outage, so a
  // sustained network/server problem produces one Rollbar event instead of one
  // per chunk. Reset whenever an upload succeeds.
  const outageReportedRef = useRef(false);

  const clearFlushTimer = useCallback(() => {
    if (flushTimerRef.current !== null) {
      window.clearInterval(flushTimerRef.current);
      flushTimerRef.current = null;
    }
  }, []);

  const haltUploads = useCallback(
    (error: unknown) => {
      if (uploadHaltedRef.current) {
        return;
      }

      uploadHaltedRef.current = true;
      // No point waking the radio on a cadence once uploads are fatally halted;
      // buffered chunks stay in IndexedDB for post-mortem/cleanup.
      clearFlushTimer();
      onUploadFatalError?.(error);
    },
    [clearFlushTimer, onUploadFatalError]
  );

  const reportUploadError = useCallback(
    (
      message: string,
      error: unknown,
      context: {
        chunkIndex?: number;
        chunkSizeBytes?: number;
        attempt?: number;
        phase?: string;
      } = {}
    ) => {
      reportRecordingError(message, error, {
        contactId,
        recordingId: recordingIdRef.current,
        hasHybridToken: Boolean(getHybridToken()),
        uploadHalted: uploadHaltedRef.current,
        ...context,
      });
    },
    [contactId, getHybridToken]
  );

  const uploadBatchWithRetry = useCallback(
    async (recordingId: string, batch: ChunkRecord[]) => {
      if (uploadHaltedRef.current || batch.length === 0) {
        return;
      }

      const lastChunkIndex = batch[batch.length - 1].chunkIndex;
      const batchSizeBytes = batch.reduce(
        (sum, chunk) => sum + chunk.blob.size,
        0
      );

      const tryUpload = async (attempt: number): Promise<void> => {
        if (uploadHaltedRef.current) {
          return;
        }

        try {
          await retailRecordingService.batchUploadChunks(
            recordingId,
            batch.map((chunk) => ({
              chunkIndex: chunk.chunkIndex,
              blob: chunk.blob,
            })),
            getHybridToken()
          );
          // Only mark chunks uploaded once the whole batch succeeds, so a failed
          // batch leaves every chunk buffered for a later retry.
          await markChunksUploaded(
            recordingId,
            batch.map((chunk) => chunk.chunkIndex)
          );
          // A success means we've recovered from any prior transient outage.
          outageReportedRef.current = false;
        } catch (error) {
          // Fatal errors (expired auth, quota, missing/invalid session) can't
          // be fixed by retrying, so stop everything and surface it to the rep.
          if (isFatalUploadError(error)) {
            reportUploadError("Retail recording batch upload failed", error, {
              chunkIndex: lastChunkIndex,
              chunkSizeBytes: batchSizeBytes,
              attempt,
              phase: "fatal",
            });
            haltUploads(error);
            throw error;
          }

          if (attempt < MAX_UPLOAD_RETRIES) {
            await sleep(INITIAL_RETRY_DELAY_MS * 2 ** (attempt - 1));
            await tryUpload(attempt + 1);
            return;
          }

          // Transient failure we've temporarily given up on for this pass. The
          // chunks stay buffered in IndexedDB (we never marked them uploaded), so
          // a later drain - triggered by the next flush tick or a resume -
          // retries them. Crucially we do NOT halt: recording continues so no
          // audio is lost. Log at most once per outage to avoid flooding Rollbar.
          if (!outageReportedRef.current) {
            outageReportedRef.current = true;
            reportUploadError(
              "Retail recording batch upload failing; will keep retrying",
              error,
              {
                chunkIndex: lastChunkIndex,
                chunkSizeBytes: batchSizeBytes,
                attempt,
                phase: "retry_exhausted_transient",
              }
            );
          }
          throw error;
        }
      };

      await tryUpload(1);
    },
    [getHybridToken, haltUploads, reportUploadError]
  );

  const drainPendingChunks = useCallback(
    async (recordingId: string) => {
      if (isUploadingRef.current || uploadHaltedRef.current) {
        return;
      }

      isUploadingRef.current = true;
      try {
        const pendingChunks = await getPendingChunks(recordingId);
        if (pendingChunks.length === 0) {
          return;
        }

        const batches = groupChunksIntoBatches(
          pendingChunks,
          BATCH_UPLOAD_MAX_BYTES
        );
        await batches.reduce<Promise<void>>(async (chain, batch) => {
          await chain;
          if (uploadHaltedRef.current) {
            return;
          }
          await uploadBatchWithRetry(recordingId, batch);
        }, Promise.resolve());
      } finally {
        isUploadingRef.current = false;
      }
    },
    [uploadBatchWithRetry]
  );

  const startFlushTimer = useCallback(
    (recordingId: string) => {
      clearFlushTimer();
      flushTimerRef.current = window.setInterval(() => {
        drainPendingChunks(recordingId).catch(() => {
          // Errors are reported/handled inside drainPendingChunks; the timer
          // keeps ticking so a later flush can retry buffered chunks.
        });
      }, BULK_UPLOAD_INTERVAL_MS);
    },
    [clearFlushTimer, drainPendingChunks]
  );

  const startUploadSession = useCallback(
    async (mimeType: string, chunkSeconds: number) => {
      // Computed once and reused for both the backend call and the local
      // session record so they can never disagree about which shift/day
      // this recording belongs to.
      const shiftDate = getLocalDateString();

      try {
        const { recordingId } = await retailRecordingService.startRecording(
          shiftDate,
          mimeType,
          chunkSeconds,
          getHybridToken()
        );
        recordingIdRef.current = recordingId;
        mimeTypeRef.current = mimeType;
        uploadHaltedRef.current = false;
        outageReportedRef.current = false;
        nextChunkIndexRef.current = 0;

        await saveChunkStorageSession({
          recordingId,
          contactId,
          mimeType,
          pendingComplete: false,
        });
        saveLocalSession({
          recordingId,
          nextChunkIndex: 0,
          localDate: shiftDate,
        });

        startFlushTimer(recordingId);

        return recordingId;
      } catch (error) {
        reportUploadError("Retail recording start failed", error, {
          phase: "start_session",
        });
        throw error;
      }
    },
    [contactId, getHybridToken, reportUploadError, startFlushTimer]
  );

  // Resumes into an already-known recordingId (e.g. from a same-day
  // localStorage session) instead of asking the backend to start a new one.
  // `resumeChunkIndex` should already include any skip-ahead buffer the
  // caller wants applied - this function just persists it and drains any
  // chunks that were captured but not yet uploaded before the interruption.
  const resumeUploadSession = useCallback(
    async (recordingId: string, mimeType: string, resumeChunkIndex: number) => {
      recordingIdRef.current = recordingId;
      mimeTypeRef.current = mimeType;
      uploadHaltedRef.current = false;
      outageReportedRef.current = false;
      nextChunkIndexRef.current = resumeChunkIndex;
      updateNextChunkIndex(recordingId, resumeChunkIndex);

      // Flush anything buffered before the interruption immediately, then fall
      // back to the periodic cadence for the rest of the session.
      startFlushTimer(recordingId);
      await drainPendingChunks(recordingId);
    },
    [drainPendingChunks, startFlushTimer]
  );

  const enqueueChunk = useCallback(
    async (blob: Blob) => {
      const recordingId = recordingIdRef.current;
      if (!recordingId) {
        throw new Error("Recording session has not been started");
      }

      const chunkIndex = nextChunkIndexRef.current;
      nextChunkIndexRef.current += 1;
      updateNextChunkIndex(recordingId, nextChunkIndexRef.current);

      // Chunks are only buffered here; the periodic flush timer uploads them in
      // bulk. This keeps the network radio idle between 5-minute flushes.
      try {
        await saveChunk(recordingId, chunkIndex, blob);
      } catch (error) {
        reportUploadError("Retail recording chunk enqueue failed", error, {
          chunkIndex,
          chunkSizeBytes: blob.size,
          phase: "save_chunk",
        });
        haltUploads(error);
        throw error;
      }
    },
    [haltUploads, reportUploadError]
  );

  const getFinalChunkIndex = useCallback(
    () => nextChunkIndexRef.current - 1,
    []
  );

  const finalizeUpload = useCallback(async () => {
    const recordingId = recordingIdRef.current;
    if (!recordingId) {
      return;
    }

    // Stop the periodic flush; this final drain handles any remaining chunks.
    clearFlushTimer();

    const expectedFinalChunkIndex = getFinalChunkIndex();

    try {
      await drainPendingChunks(recordingId);
      await retailRecordingService.completeRecording(
        recordingId,
        expectedFinalChunkIndex,
        getHybridToken()
      );
      await clearChunkStorageSession(recordingId);
      clearLocalSession();
      recordingIdRef.current = null;
      mimeTypeRef.current = null;
      nextChunkIndexRef.current = 0;
    } catch (error) {
      await saveChunkStorageSession({
        recordingId,
        contactId,
        mimeType: mimeTypeRef.current ?? "audio/webm",
        pendingComplete: true,
        expectedFinalChunkIndex,
      });
      reportUploadError("Retail recording complete failed", error, {
        chunkIndex: expectedFinalChunkIndex,
        phase: "complete_session",
      });
      haltUploads(error);
      throw error;
    }
  }, [
    contactId,
    clearFlushTimer,
    drainPendingChunks,
    getFinalChunkIndex,
    getHybridToken,
    reportUploadError,
    haltUploads,
  ]);

  // Ensure the flush interval never outlives the hook (e.g. tab close, leader
  // handoff that unmounts this hook).
  useEffect(() => clearFlushTimer, [clearFlushTimer]);

  return {
    startUploadSession,
    resumeUploadSession,
    enqueueChunk,
    finalizeUpload,
    getRecordingId: () => recordingIdRef.current,
  };
};

export { useChunkedAudioUpload };
